import { FIELD_TYPES, SELECTABLE_TYPES } from "./types";

const randomSuffix = () => {
  const cryptoObj = typeof window !== "undefined" ? window.crypto : undefined;
  if (cryptoObj?.getRandomValues) {
    const buf = new Uint32Array(1);
    cryptoObj.getRandomValues(buf);
    return buf[0].toString(36).slice(0, 5);
  }
  return String(performance.now()).replace(".", "").slice(-5);
};

export const createId = (prefix = "id") =>
  `${prefix}_${Date.now()}_${randomSuffix()}`;

export const newOption = (label = "Option") => ({
  value: createId("opt"),
  label,
  sub_questions: [],
});

export const TABLE_COLUMN_TYPES = [
  "text",
  "textarea",
  "number",
  "date",
  "datetime",
  "select",
  "multiselect",
  "checkbox",
  "radio",
  "file",
];

export const TABLE_COLUMN_SELECTABLE_TYPES = [
  "select",
  "multiselect",
  "checkbox",
  "radio",
];

export const newTableColumn = (label = "Column 1", type = "text") => {
  const col = { id: createId("col"), label, type };
  if (TABLE_COLUMN_SELECTABLE_TYPES.includes(type)) {
    col.options = [newOption("Option 1")];
  }
  return col;
};

export const newField = (type = FIELD_TYPES.TEXT) => {
  const base = {
    id: createId("f"),
    type,
    label: "",
    required: false,
  };
  if (
    [
      FIELD_TYPES.TEXT,
      FIELD_TYPES.NUMBER,
      FIELD_TYPES.SELECT,
      FIELD_TYPES.MULTISELECT,
    ].includes(type)
  ) {
    base.placeholder = "";
  }
  if (SELECTABLE_TYPES.includes(type)) {
    base.options = [newOption("Option 1")];
  }
  if (type === FIELD_TYPES.NUMBER) {
    base.min = undefined;
    base.max = undefined;
  }
  if (type === FIELD_TYPES.SLIDER) {
    base.min = 0;
    base.max = 10;
    base.step = 1;
  }
  if (type === FIELD_TYPES.TABLE) {
    base.columns = [newTableColumn("Column 1", "text")];
    base.min_rows = 0;
    base.max_rows = 50;
    base.allow_add_rows = true;
  }
  if (type === FIELD_TYPES.SECTION_HEADER) {
    base.description = "";
  }
  return base;
};

export const newSection = (index) => ({
  id: createId("s"),
  title: `Section ${index}`,
  repeatable: false,
  version: 1,
  fields: [],
});

export const newForm = (name = "New Form", mode = "IP") => ({
  id: createId("f"),
  name,
  mode,
  active: true,
  version: 1,
  sections: [newSection(1)],
});

export const newRole = (name) => ({
  id: createId("r"),
  name,
  mode: "IP",
  forms: [],
});

const DEFAULT_FORMS = [
  { name: "Initial Assessment", repeatable: false },
  { name: "Review Form", repeatable: true },
];
const SEED_MODES = ["IP", "OP", "BOTH"];

const seedForm = ({ name, repeatable }, mode) => ({
  id: createId("f"),
  name,
  system: true,
  active: true,
  repeatable,
  mode,
  version: 1,
  sections: [newSection(1)],
});

export const defaultRoleForms = () =>
  SEED_MODES.flatMap((mode) =>
    DEFAULT_FORMS.map((form) => seedForm(form, mode))
  );

const norm = (s) => (s || "").trim().toLowerCase();

const seedDefaultsForModes = (forms, modes) => {
  const additions = [];
  modes.forEach((mode) => {
    const forMode = forms.filter((f) => f.mode === mode);
    DEFAULT_FORMS.forEach((tpl) => {
      const alreadyPresent = forMode.some((f) => norm(f.name) === norm(tpl.name));
      if (!alreadyPresent) additions.push(seedForm(tpl, mode));
    });
  });
  return [...additions, ...forms];
};

export const withDefaultRoleForms = (forms = []) =>
  seedDefaultsForModes(forms, SEED_MODES);

export const withDefaultOpRoleForms = (forms = []) =>
  seedDefaultsForModes(forms, ["OP"]);

export const buildFormPayload = (form) => ({
  version: form.version || 1,
  sections: form.sections,
});

const cloneFieldWithNewIds = (field) => {
  const next = { ...field, id: createId("f") };
  if (Array.isArray(field.options)) {
    next.options = field.options.map((opt) => ({
      ...opt,
      value: createId("opt"),
      sub_questions: (opt.sub_questions || []).map(cloneFieldWithNewIds),
    }));
  }
  if (Array.isArray(field.sub_questions)) {
    next.sub_questions = field.sub_questions.map(cloneFieldWithNewIds);
  }
  if (Array.isArray(field.columns)) {
    next.columns = field.columns.map((col) => ({ ...col, id: createId("col") }));
  }
  return next;
};

export const cloneFormWithNewIds = (form, overrides = {}) => ({
  ...form,
  id: createId("f"),
  active: true,
  ...overrides,
  sections: (form.sections || []).map((section) => ({
    ...section,
    id: createId("s"),
    fields: (section.fields || []).map(cloneFieldWithNewIds),
  })),
});
